import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchUserCustomMetrics,
  fetchSuggestedMetrics,
  addUserCustomMetric,
  updateUserCustomMetric,
  deleteUserCustomMetric,
  clearError as clearErrorAction
} from '../store/slices/customMetricsSlice';
import { CustomMetric } from '../services/firebase/customMetrics.service';

export const useCustomMetrics = () => {
  const dispatch = useAppDispatch();
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  
  // Get the custom metrics state from Redux
  const {
    customMetrics,
    suggestedMetrics,
    sortedCustomMetrics,
    sortedSuggestedMetrics,
    activeMetrics,
    isLoading: storeLoading,
    error: storeError
  } = useAppSelector((state) => state.customMetrics);
  
  // Clear error
  const clearError = useCallback(() => {
    setLocalError(null);
    dispatch(clearErrorAction());
  }, [dispatch]);
  
  // Get user's custom metrics
  const getUserMetrics = useCallback(async (userId: string) => {
    setIsLocalLoading(true);
    setLocalError(null);
    
    try {
      const resultAction = await dispatch(fetchUserCustomMetrics(userId));
      if (fetchUserCustomMetrics.fulfilled.match(resultAction)) {
        setIsLocalLoading(false);
        return resultAction.payload;
      }
      
      setIsLocalLoading(false);
      if (resultAction.payload) {
        setLocalError(resultAction.payload as string);
      }
      return null;
    } catch (err: any) {
      setIsLocalLoading(false);
      setLocalError(err.message || 'Failed to load custom metrics');
      return null;
    }
  }, [dispatch]);
  
  // Get suggested metrics
  const getSuggestedMetrics = useCallback(async () => {
    setIsLocalLoading(true);
    setLocalError(null);
    
    try {
      const resultAction = await dispatch(fetchSuggestedMetrics());
      if (fetchSuggestedMetrics.fulfilled.match(resultAction)) {
        setIsLocalLoading(false);
        return resultAction.payload;
      }
      
      setIsLocalLoading(false);
      if (resultAction.payload) {
        setLocalError(resultAction.payload as string);
      }
      return null;
    } catch (err: any) {
      setIsLocalLoading(false);
      setLocalError(err.message || 'Failed to load suggested metrics');
      return null;
    }
  }, [dispatch]);
  
  // Add a new custom metric
  const addMetric = useCallback(async (userId: string, metric: CustomMetric) => {
    setIsLocalLoading(true);
    setLocalError(null);
    
    try {
      const resultAction = await dispatch(addUserCustomMetric({ userId, metric }));
      if (addUserCustomMetric.fulfilled.match(resultAction)) {
        setIsLocalLoading(false);
        return resultAction.payload;
      }
      
      setIsLocalLoading(false);
      if (resultAction.payload) {
        setLocalError(resultAction.payload as string);
      }
      return null;
    } catch (err: any) {
      setIsLocalLoading(false);
      setLocalError(err.message || 'Failed to add custom metric');
      return null;
    }
  }, [dispatch]);
  
  // Update an existing custom metric
  const updateMetric = useCallback(async (userId: string, metricId: string, metric: Partial<CustomMetric>) => {
    setIsLocalLoading(true);
    setLocalError(null);
    
    try {
      const resultAction = await dispatch(updateUserCustomMetric({ userId, metricId, metric }));
      if (updateUserCustomMetric.fulfilled.match(resultAction)) {
        setIsLocalLoading(false);
        return resultAction.payload;
      }
      
      setIsLocalLoading(false);
      if (resultAction.payload) {
        setLocalError(resultAction.payload as string);
      }
      return null;
    } catch (err: any) {
      setIsLocalLoading(false);
      setLocalError(err.message || 'Failed to update custom metric');
      return null;
    }
  }, [dispatch]);
  
  // Delete a custom metric
  const deleteMetric = useCallback(async (userId: string, metricId: string) => {
    setIsLocalLoading(true);
    setLocalError(null);
    
    try {
      const resultAction = await dispatch(deleteUserCustomMetric({ userId, metricId }));
      if (deleteUserCustomMetric.fulfilled.match(resultAction)) {
        setIsLocalLoading(false);
        return resultAction.payload;
      }
      
      setIsLocalLoading(false);
      if (resultAction.payload) {
        setLocalError(resultAction.payload as string);
      }
      return null;
    } catch (err: any) {
      setIsLocalLoading(false);
      setLocalError(err.message || 'Failed to delete custom metric');
      return null;
    }
  }, [dispatch]);
  
  // Toggle a metric's tracked status
  const toggleMetricTracking = useCallback(async (userId: string, metricId: string, isTracked: boolean) => {
    return updateMetric(userId, metricId, { isTracked });
  }, [updateMetric]);
  
  // Update a metric's value (for active metrics during training)
  const updateMetricValue = useCallback(async (userId: string, metricId: string, value: number) => {
    return updateMetric(userId, metricId, { value });
  }, [updateMetric]);
  
  return {
    // State
    customMetrics,
    suggestedMetrics,
    sortedCustomMetrics,
    sortedSuggestedMetrics,
    activeMetrics,
    isLoading: isLocalLoading || storeLoading,
    error: localError || storeError,
    
    // Actions
    getUserMetrics,
    getSuggestedMetrics,
    addMetric,
    updateMetric,
    deleteMetric,
    toggleMetricTracking,
    updateMetricValue,
    clearError
  };
};

export default useCustomMetrics;